import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  User, MapPin, CalendarClock, Clock, RefreshCw, AlertCircle, Truck,
  Gift, ChevronDown, ArrowRight, ArrowLeft,
} from "lucide-react";

import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { useCart } from "../../lib/CartContext";
import { useCustomerAuth } from "../../lib/CustomerAuthContext";
import { cn } from "../../lib/utils";
import { TURKEY_LOCATIONS } from "../../data/turkeyLocations";
import { useCartFlow } from "./CartLayout";
import { DateChipPicker, SlotChipPicker, todayISO } from "./pickers";

const CARD_EMOJIS = ["💐", "🌹", "🎂", "😊", "💖", "🎉", "🙏", "✨"];

export default function CartDeliveryStep() {
  const { cart: items } = useCart();
  const { customer, isLoggedIn } = useCustomerAuth();
  const navigate = useNavigate();
  const {
    form, handleChange, handleIlChange, handleSlotSelect, appendEmoji,
    ilceOptions, isDeliveryToday, allSlotsPassedToday,
    notesOpen, setNotesOpen,
    setAuthOpen,
    zoneInfo, zoneLoading,
    dateError, setDateError, slotError, setSlotError,
    setForm,
  } = useCartFlow();

  const subtotal = items.reduce((sum, i) => sum + i.price * i.qty, 0);
  const [emailError, setEmailError] = useState("");

  useEffect(() => {
    if (items.length === 0) navigate("/sepet", { replace: true });
  }, [items.length, navigate]);

  if (items.length === 0) return null;

  const submit = (e) => {
    e.preventDefault();
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setEmailError("Geçerli bir e-posta adresi girin ya da alanı boş bırakın.");
      return;
    }
    setEmailError("");
    if (!form.deliveryDate) {
      setDateError("Lütfen bir teslimat tarihi seçin.");
      return;
    }
    setDateError("");
    const slotRequired = !zoneInfo?.requires_date_only && zoneInfo?.slots?.length > 0 && !allSlotsPassedToday;
    if (slotRequired && !(form.deliveryStart && form.deliveryEnd)) {
      setSlotError("Lütfen bir teslimat saati seçin.");
      return;
    }
    setSlotError("");
    navigate("/sepet/onayla");
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-10">
      <div className="lg:col-span-2 space-y-5">
        <h1 className="font-serif text-3xl mb-1">Teslimat Bilgileri</h1>
        <p className="text-sm text-[#888888] mb-6">Çiçeğin kime, nereye ve ne zaman ulaşacağını bize söyleyin.</p>

        {isLoggedIn ? (
          <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-[#F5E9DD] text-sm text-[#A85C34]" data-testid="checkout-logged-in-badge">
            <User className="w-4 h-4 shrink-0" />
            Merhaba, <span className="font-medium">{customer.name}</span> — bilgileriniz otomatik dolduruldu.
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAuthOpen(true)}
            className="w-full flex items-center justify-between gap-2.5 px-4 py-3 rounded-xl bg-[#F8F8F8] hover:bg-[#EAEAEA] text-sm text-[#222222] transition-colors text-left"
            data-testid="checkout-login-nudge"
          >
            <span className="flex items-center gap-2.5">
              <User className="w-4 h-4 text-[#A85C34] shrink-0" />
              Hızlı sipariş için giriş yapın (isteğe bağlı)
            </span>
            <span className="text-[#A85C34] font-medium shrink-0">Giriş Yap</span>
          </button>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-[#222222] mb-2 block">Alıcı Adı Soyadı</label>
            <Input
              required
              value={form.name}
              onChange={handleChange("name")}
              placeholder="Adı Soyadı"
              className="h-12 rounded-xl border-[#EAEAEA] focus-visible:ring-[#A85C34]"
              data-testid="checkout-name-input"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-[#222222] mb-2 block">Telefon</label>
            <Input
              required
              type="tel"
              value={form.phone}
              onChange={handleChange("phone")}
              placeholder="0532 000 00 00"
              className="h-12 rounded-xl border-[#EAEAEA] focus-visible:ring-[#A85C34]"
              data-testid="checkout-phone-input"
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-[#222222] mb-2 block">E-posta (İsteğe bağlı)</label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => { handleChange("email")(e); setEmailError(""); }}
            placeholder="ornek@eposta.com"
            className="h-12 rounded-xl border-[#EAEAEA] focus-visible:ring-[#A85C34]"
            data-testid="checkout-email-input"
          />
          {emailError && <p className="text-xs text-red-500 mt-1.5">{emailError}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-[#222222] mb-2 block">İl</label>
            <select
              required
              value={form.il}
              onChange={handleIlChange}
              className="w-full h-12 px-3 rounded-xl border border-[#EAEAEA] bg-white text-sm text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#A85C34]"
              data-testid="checkout-il-select"
            >
              <option value="">İl seçin</option>
              {TURKEY_LOCATIONS.map((p) => (
                <option key={p.id} value={p.il}>{p.il}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-[#222222] mb-2 block">İlçe</label>
            <select
              required
              disabled={!form.il}
              value={form.ilce}
              onChange={handleChange("ilce")}
              className="w-full h-12 px-3 rounded-xl border border-[#EAEAEA] bg-white text-sm text-[#222222] focus:outline-none focus:ring-2 focus:ring-[#A85C34] disabled:bg-[#F8F8F8] disabled:text-[#888888]"
              data-testid="checkout-ilce-select"
            >
              <option value="">{form.il ? "İlçe seçin" : "Önce il seçin"}</option>
              {ilceOptions.map((d) => (
                <option key={d.id} value={d.name}>{d.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-[#222222] mb-2 block">Adres Detayı</label>
          <Textarea
            required
            value={form.addressDetail}
            onChange={handleChange("addressDetail")}
            placeholder="Mahalle, sokak, bina no, daire, tarif"
            className="min-h-[80px] rounded-xl border-[#EAEAEA] focus-visible:ring-[#A85C34]"
            data-testid="checkout-address-input"
          />
        </div>

        {form.il && (
          <div className="rounded-xl bg-[#F8F8F8] px-4 py-3 text-sm space-y-1.5" data-testid="checkout-zone-status">
            {zoneLoading ? (
              <span className="flex items-center gap-2 text-[#888888]">
                <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Teslimat bilgisi kontrol ediliyor...
              </span>
            ) : zoneInfo?.matched ? (
              <>
                {zoneInfo.requires_date_only ? (
                  <span className="flex items-center gap-2 text-[#A85C34]">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" /> Bu bölge için sadece teslimat tarihi seçilir, saat aralığı yoktur.
                  </span>
                ) : zoneInfo.same_day_possible ? (
                  <span className="flex items-center gap-2 text-emerald-700">
                    <Clock className="w-3.5 h-3.5 shrink-0" /> Bugün teslimat mümkün{zoneInfo.cutoff_time ? ` (kesim saati ${zoneInfo.cutoff_time})` : ""}.
                  </span>
                ) : (
                  <span className="flex items-center gap-2 text-[#A85C34]">
                    <Clock className="w-3.5 h-3.5 shrink-0" /> Kesim saati geçti — en erken {new Date(zoneInfo.earliest_date + "T00:00:00").toLocaleDateString("tr-TR", { day: "numeric", month: "long" })} teslim edilir.
                  </span>
                )}
                {zoneInfo.courier_type === "kargo" && (
                  <span className="flex items-center gap-2 text-[#888888]">
                    <Truck className="w-3.5 h-3.5 shrink-0" /> Bu bölgeye teslimat kargo ile gerçekleştirilir.
                  </span>
                )}
              </>
            ) : (
              <span className="flex items-center gap-2 text-[#888888]">
                <MapPin className="w-3.5 h-3.5 shrink-0" /> Seçtiğiniz bölge için özel bir teslimat kuralı yok, ekibimiz sizinle iletişime geçecek.
              </span>
            )}
          </div>
        )}

        <div>
          <label className="text-sm font-medium text-[#222222] mb-2 flex items-center gap-1.5">
            <CalendarClock className="w-3.5 h-3.5 text-[#A85C34]" /> Teslimat Tarihi
          </label>
          <DateChipPicker
            minDateISO={zoneInfo?.earliest_date || todayISO()}
            value={form.deliveryDate}
            onChange={(iso) => { setForm((f) => ({ ...f, deliveryDate: iso, deliveryStart: "", deliveryEnd: "" })); setDateError(""); }}
          />
          {dateError && <p className="text-xs text-red-500 mt-1.5">{dateError}</p>}
        </div>

        {!zoneInfo?.requires_date_only && zoneInfo?.slots?.length > 0 && (
          <div>
            <label className="text-sm font-medium text-[#222222] mb-2 block">Teslimat Saati</label>
            <SlotChipPicker
              slots={zoneInfo.slots}
              isToday={isDeliveryToday}
              value={form.deliveryStart && form.deliveryEnd ? `${form.deliveryStart}-${form.deliveryEnd}` : ""}
              onChange={handleSlotSelect}
            />
            {allSlotsPassedToday && (
              <p className="text-xs text-[#888888] flex items-center gap-1.5 mt-2" data-testid="checkout-slots-passed">
                <Clock className="w-3.5 h-3.5 shrink-0" /> Bugün için tüm teslimat saatleri geçti — başka bir tarih seçin.
              </p>
            )}
            {slotError && <p className="text-xs text-red-500 mt-2">{slotError}</p>}
          </div>
        )}

        <div>
          <label className="text-sm font-medium text-[#222222] mb-2 block">Gönderen Adı (İsteğe bağlı)</label>
          <Input
            value={form.senderName}
            onChange={handleChange("senderName")}
            placeholder="Kart üzerinde görünecek isim"
            className="h-12 rounded-xl border-[#EAEAEA] focus-visible:ring-[#A85C34]"
            data-testid="checkout-sender-input"
          />
        </div>

        <div className="rounded-xl border border-[#EAEAEA] overflow-hidden">
          <button
            type="button"
            onClick={() => setNotesOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 bg-[#F8F8F8] text-sm font-medium text-[#222222]"
            data-testid="checkout-notes-toggle"
          >
            <span className="flex items-center gap-2">
              <Gift className="w-4 h-4 text-[#A85C34]" />
              Hazır Notlar (Kart Mesajı)
            </span>
            <ChevronDown className={cn("w-4 h-4 transition-transform", notesOpen && "rotate-180")} />
          </button>
          {notesOpen && (
            <div className="p-4 space-y-3">
              <Textarea
                value={form.note}
                onChange={handleChange("note")}
                placeholder="Kartınıza yazılacak mesaj..."
                className="min-h-[60px] rounded-xl border-[#EAEAEA] focus-visible:ring-[#A85C34]"
                data-testid="checkout-note-input"
              />
              <div className="flex flex-wrap gap-2">
                {CARD_EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => appendEmoji(emoji)}
                    className="w-9 h-9 rounded-full bg-[#F8F8F8] hover:bg-[#EAEAEA] flex items-center justify-center text-base transition-colors"
                    data-testid={`checkout-note-emoji-${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="lg:col-span-1">
        <div className="lg:sticky lg:top-8 rounded-2xl border border-[#EAEAEA] p-6 space-y-4">
          <h2 className="font-serif text-lg">Sipariş Özeti</h2>
          <div className="flex items-baseline justify-between border-t border-[#EAEAEA] pt-4">
            <span className="text-[#555555]">Ara Toplam</span>
            <span className="font-serif text-2xl font-bold text-[#222222]">{subtotal.toLocaleString("tr-TR")}₺</span>
          </div>
          <button
            type="submit"
            className="w-full flex items-center justify-center gap-2 bg-[#A85C34] hover:bg-[#8B4726] text-white rounded-full px-6 py-4 font-medium transition-colors"
            data-testid="checkout-continue"
          >
            Devam Et
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => navigate("/sepet")}
            className="w-full flex items-center justify-center gap-2 text-sm text-[#888888] hover:text-[#222222] transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Sepete Dön
          </button>
        </div>
      </div>
    </form>
  );
}
